import { supabase } from './supabase'

// ── Series config ─────────────────────────────────────────────────────────────

export type SeriesId =
  | 'T10Y2Y' | 'T10YIE' | 'DGS10' | 'DGS2'
  | 'DCOILWTICO' | 'BAMLH0A0HYM2' | 'VIXCLS'
  | 'DEXUSEU' | 'DEXUSUK'
  | 'FEDFUNDS' | 'CPIAUCSL' | 'PCEPI' | 'PCEPILFE'
  | 'UNRATE' | 'M2SL' | 'USREC'
  | 'GDP'

interface SeriesMeta {
  label: string
  unit: string
  cacheTtl: number   // ms
  history: number    // observations to fetch for sparklines/calcs
  group: 'rates' | 'inflation' | 'stress'
  decimals: number
  suffix?: string
}

// TTL : 24 HEURES MAXIMUM POUR TOUTES LES SERIES.
//
// Les series mensuelles etaient cachees 30 jours et GDP 90 jours. C'est
// exactement le bug corrige cote serveur : un cache de 30 jours sur une serie
// mensuelle fait RATER la publication. Le CPI de juillet sort mi-aout, et le
// cache servait encore celui de juin — l'utilisateur voyait un chiffre d'un mois
// de retard sans aucun signe. Le correctif serveur n'avait pas ete reporte ici.
//
// FRED est gratuit et sans limite de debit : aucun TTL superieur a 24 h n'est
// defendable. Ne remontez pas ces valeurs.
const TTL_24H = 86400e3

export const SERIES_META: Record<SeriesId, SeriesMeta> = {
  // Rates
  FEDFUNDS:     { label: 'Fed Funds Rate',       unit: '%',    cacheTtl: TTL_24H,       history: 13, group: 'rates',     decimals: 2 },
  DGS2:         { label: 'Taux 2 ans (US)',       unit: '%',    cacheTtl: 86400e3,       history: 13, group: 'rates',     decimals: 2 },
  DGS10:        { label: 'Taux 10 ans (US)',      unit: '%',    cacheTtl: 86400e3,       history: 13, group: 'rates',     decimals: 2 },
  T10Y2Y:       { label: 'Spread 10Y-2Y',         unit: '%',    cacheTtl: 86400e3,       history: 13, group: 'rates',     decimals: 2 },
  T10YIE:       { label: 'Inflation implicite',   unit: '%',    cacheTtl: 86400e3,       history: 13, group: 'rates',     decimals: 2 },
  // Inflation & Growth
  CPIAUCSL:     { label: 'CPI (YoY calc)',        unit: '%',    cacheTtl: TTL_24H,        history: 13, group: 'inflation', decimals: 2 },
  PCEPI:        { label: 'PCE Inflation',         unit: 'idx',  cacheTtl: TTL_24H,        history: 13, group: 'inflation', decimals: 2 },
  PCEPILFE:     { label: 'Core PCE',              unit: 'idx',  cacheTtl: TTL_24H,        history: 13, group: 'inflation', decimals: 2 },
  GDP:          { label: 'GDP (US)',              unit: '$B',   cacheTtl: TTL_24H,        history: 8,  group: 'inflation', decimals: 0 },
  M2SL:         { label: 'M2 Money Supply',       unit: '$B',   cacheTtl: TTL_24H,        history: 13, group: 'inflation', decimals: 0 },
  UNRATE:       { label: 'Chômage (US)',           unit: '%',    cacheTtl: TTL_24H,        history: 13, group: 'inflation', decimals: 1 },
  // Market Stress
  VIXCLS:       { label: 'VIX',                  unit: '',     cacheTtl: 86400e3,       history: 13, group: 'stress',    decimals: 1 },
  BAMLH0A0HYM2: { label: 'HY Credit Spread',     unit: '%',    cacheTtl: 86400e3,       history: 13, group: 'stress',    decimals: 2 },
  DCOILWTICO:   { label: 'WTI Crude Oil',         unit: '$',    cacheTtl: 86400e3,       history: 13, group: 'stress',    decimals: 2 },
  DEXUSEU:      { label: 'EUR/USD (FRED)',         unit: '',     cacheTtl: 86400e3,       history: 13, group: 'stress',    decimals: 4 },
  DEXUSUK:      { label: 'GBP/USD (FRED)',         unit: '',     cacheTtl: 86400e3,       history: 13, group: 'stress',    decimals: 4 },
  USREC:        { label: 'Récession US',          unit: '',     cacheTtl: TTL_24H,        history: 2,  group: 'stress',    decimals: 0 },
}

export const ALL_SERIES = Object.keys(SERIES_META) as SeriesId[]

// ── Cache helpers (localStorage) ─────────────────────────────────────────────

const CACHE_PREFIX = 'fred_v1_'

interface CacheEntry {
  observations: { date: string; value: string }[]
  ts: number
}

function cacheKey(id: SeriesId) { return CACHE_PREFIX + id }

export function readCache(id: SeriesId): CacheEntry | null {
  try {
    const raw = localStorage.getItem(cacheKey(id))
    if (!raw) return null
    return JSON.parse(raw) as CacheEntry
  } catch { return null }
}

function writeCache(id: SeriesId, obs: { date: string; value: string }[]) {
  try {
    const entry: CacheEntry = { observations: obs, ts: Date.now() }
    localStorage.setItem(cacheKey(id), JSON.stringify(entry))
  } catch {}
}

function isCacheValid(id: SeriesId, entry: CacheEntry): boolean {
  return Date.now() - entry.ts < SERIES_META[id].cacheTtl
}

// ── Fetch single series ───────────────────────────────────────────────────────

async function fetchSeries(
  id: SeriesId,
  supaUrl: string,
  headers: Record<string, string>
): Promise<{ date: string; value: string }[]> {
  const meta = SERIES_META[id]
  const res = await fetch(
    `${supaUrl}/functions/v1/fred-proxy?series_id=${id}&limit=${meta.history}`,
    { headers }
  )
  if (!res.ok) return []
  const json = await res.json()
  return Array.isArray(json?.observations) ? json.observations : []
}

// ── Public: fetch all, respecting cache ──────────────────────────────────────

export interface SeriesData {
  id: SeriesId
  observations: { date: string; value: string }[]
  latest: number | null
  prev: number | null
  history: number[]   // numeric values, oldest first, filtered for valid
}

function parseObs(obs: { date: string; value: string }[]): number[] {
  return obs
    .map(o => parseFloat(o.value))
    .filter(v => isFinite(v))
    .reverse() // oldest → newest
}

function getLatestTwo(obs: { date: string; value: string }[]) {
  const valid = obs.filter(o => o.value !== '.' && isFinite(parseFloat(o.value)))
  const latest = valid[0] ? parseFloat(valid[0].value) : null
  const prev   = valid[1] ? parseFloat(valid[1].value) : null
  return { latest, prev }
}

export async function fetchAllSeries(
  supaUrl: string,
  force = false
): Promise<Record<SeriesId, SeriesData>> {
  const { data: session } = await supabase.auth.getSession()
  const supaKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string
  const headers: Record<string, string> = {
    Authorization: `Bearer ${session.session?.access_token ?? supaKey}`,
    'Content-Type': 'application/json',
    Apikey: supaKey,
  }

  const results = await Promise.all(
    ALL_SERIES.map(async (id) => {
      const cached = readCache(id)
      let obs: { date: string; value: string }[]

      if (!force && cached && isCacheValid(id, cached)) {
        obs = cached.observations
      } else {
        obs = await fetchSeries(id, supaUrl, headers)
        if (obs.length) writeCache(id, obs)
        else if (cached) obs = cached.observations // fallback
      }

      const { latest, prev } = getLatestTwo(obs)
      const history = parseObs(obs)
      return { id, observations: obs, latest, prev, history } as SeriesData & { id: SeriesId }
    })
  )

  return Object.fromEntries(results.map(r => [r.id, r])) as Record<SeriesId, SeriesData>
}

// ── Derived calculations ──────────────────────────────────────────────────────

export function calcCpiYoY(cpiData: SeriesData): number | null {
  const h = cpiData.history
  if (h.length < 13) return null
  const latest = h[h.length - 1]
  const yearAgo = h[h.length - 13]
  if (!yearAgo) return null
  return ((latest / yearAgo) - 1) * 100
}

export type SpreadStatus = 'normal' | 'inverted' | 'severely_inverted'
export function spreadStatus(val: number | null): SpreadStatus {
  if (val == null) return 'normal'
  if (val < -0.5) return 'severely_inverted'
  if (val < 0)    return 'inverted'
  return 'normal'
}

export type RiskMode = 'Risk-ON' | 'Neutral' | 'Risk-OFF'
export function calcRiskMode(
  vix: number | null,
  hySpread: SeriesData | null,
  t10y2y: number | null
): RiskMode {
  let score = 0
  if (vix != null) {
    if (vix < 15) score += 1
    else if (vix > 25) score -= 1
  }
  if (hySpread && hySpread.history.length >= 2) {
    const last = hySpread.history[hySpread.history.length - 1]
    const prev = hySpread.history[hySpread.history.length - 2]
    if (last < prev) score += 1   // tightening = positive
    else if (last > prev) score -= 1
  }
  if (t10y2y != null) {
    if (t10y2y > 0) score += 1
    else score -= 1
  }
  if (score >= 2) return 'Risk-ON'
  if (score <= -1) return 'Risk-OFF'
  return 'Neutral'
}

export type DxyTrend = 'strengthening' | 'weakening' | 'stable'
export function calcDxyTrend(
  eurusd: SeriesData | null,
  gbpusd: SeriesData | null
): DxyTrend {
  // DXY moves inversely to EUR/USD and GBP/USD
  const euTrend = eurusd && eurusd.history.length >= 2
    ? eurusd.history[eurusd.history.length - 1] - eurusd.history[0]
    : 0
  const gbTrend = gbpusd && gbpusd.history.length >= 2
    ? gbpusd.history[gbpusd.history.length - 1] - gbpusd.history[0]
    : 0
  const combined = euTrend * 0.576 + gbTrend * 0.119  // approximate DXY weights
  if (combined < -0.005) return 'strengthening'  // EUR/GBP down → USD up
  if (combined > 0.005)  return 'weakening'
  return 'stable'
}

export function calcM2Trend(m2: SeriesData): { change3m: number | null; signal: 'bullish' | 'bearish' | 'neutral' } {
  const h = m2.history
  if (h.length < 4) return { change3m: null, signal: 'neutral' }
  const latest = h[h.length - 1]
  const ago3   = h[h.length - 4]
  const change3m = ((latest / ago3) - 1) * 100
  if (change3m > 0.5) return { change3m, signal: 'bullish' }
  if (change3m < -0.2) return { change3m, signal: 'bearish' }
  return { change3m, signal: 'neutral' }
}
